import { useEffect, useMemo, useState } from 'react';
import { api } from '../../lib/api';
import { Input } from '../../components/ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Loader2, Search, AlertTriangle, MapPinOff, Clock, TrendingDown, Scale, Inbox, RefreshCw, PencilLine } from 'lucide-react';
import { useFuels } from '../../lib/fuels';
import { ManualPriceChangeModal } from '../../components/ManualPriceChangeModal';

const FLAG_META = {
  below:    { label: 'Bajo sugerido',      icon: TrendingDown, cls: 'bg-[#fee2e2] text-[#991b1b]' },
  mismatch: { label: 'Distinto al sugerido', icon: Scale,      cls: 'bg-[#fef3c7] text-[#92400e]' },
  stale:    { label: 'Sin captura >48h',   icon: Clock,        cls: 'bg-[#e8e5de] text-[#5a5a5a]' },
  gps:      { label: 'Anomalía GPS',       icon: MapPinOff,    cls: 'bg-[#fde8ff] text-[#86198f]' },
  no_data:  { label: 'Sin datos',          icon: AlertTriangle, cls: 'bg-[#f3f0e9] text-[#8a8579]' },
};
const ALLV = '__all__';

const fmtQ = (v) => (v == null ? '—' : `Q${Number(v).toFixed(2)}`);
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString('es-GT', { dateStyle: 'short', timeStyle: 'short' }) : '—');

const StationDetail = ({ row, fuels, onChangePrices }) => {
  if (!row) return null;
  const fuelIds = fuels.filter(f => row.prices[f.id] || row.suggested[f.id]).map(f => f.id);
  return (
    <div data-testid="monitor-detail">
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div>
          <p className="text-[10px] tracking-wider uppercase text-[#8a8579]">{row.brand}{row.bu ? ` · BU ${row.bu}` : ''} · {row.is_own ? 'Propia' : 'Competencia'}</p>
          <h3 className="font-serif text-[22px] leading-tight tracking-tight">{row.name}</h3>
          <p className="text-[11.5px] text-[#8a8579] mt-0.5">{[row.department, row.zone, row.tm && `TM: ${row.tm}`].filter(Boolean).join(' · ') || '—'}</p>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {row.flags.map(f => {
            const m = FLAG_META[f]; if (!m) return null; const I = m.icon;
            return <span key={f} className={`inline-flex items-center gap-1 text-[10.5px] px-2 py-0.5 rounded-full ${m.cls}`}><I size={11}/>{m.label}</span>;
          })}
        </div>
      </div>

      <div className="mt-4 bg-white border border-[#e8e5de] rounded-xl">
        <table className="w-full text-[12px]">
          <thead>
            <tr className="text-[10.5px] uppercase tracking-wider text-[#8a8579] border-b border-[#e8e5de]">
              <th className="text-left py-2.5 px-3 font-normal">Producto</th>
              <th className="text-right py-2.5 px-2 font-normal">Auto</th>
              <th className="text-right py-2.5 px-3 font-normal">Completo</th>
            </tr>
          </thead>
          <tbody>
            {fuelIds.length === 0 && (
              <tr><td colSpan={3} className="py-6 text-center text-[#8a8579]">Sin precios registrados.</td></tr>
            )}
            {fuelIds.map(fid => {
              const f = fuels.find(x => x.id === fid);
              const cell = (svc, lastCol) => {
                const cv = row.prices[fid]?.[svc];
                const sv = row.suggested[fid]?.[svc];
                const d = cv != null && sv != null ? +(cv - sv).toFixed(2) : null;
                const cls = d == null ? '' : d < -0.009 ? 'text-[#991b1b] font-medium' : Math.abs(d) >= 0.01 ? 'text-[#92400e]' : 'text-[#166534]';
                return (
                  <div className={`flex flex-col items-end leading-tight ${lastCol ? 'pr-1' : ''}`}>
                    <span className={`font-mono tabular-nums whitespace-nowrap ${cls}`}>{fmtQ(cv)}{d != null && d !== 0 ? <span className="text-[10px] ml-1">({d > 0 ? '+' : ''}{d.toFixed(2)})</span> : null}</span>
                    {row.is_own && sv != null && (
                      <span className="text-[10px] text-[#8a8579] font-mono tabular-nums whitespace-nowrap">sug. {fmtQ(sv)}</span>
                    )}
                  </div>
                );
              };
              return (
                <tr key={fid} className="border-b border-[#f3f0e9] last:border-0" data-testid={`detail-fuel-${fid}`}>
                  <td className="py-2 px-3"><span className="flex items-center gap-2"><span className="w-1.5 h-4 rounded-full" style={{ background: f?.color || '#8a8579' }}/>{f?.name || fid}</span></td>
                  <td className="py-2 px-2 text-right">{cell('self')}</td>
                  <td className="py-2 px-3 text-right">{cell('full', true)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-2 text-[11.5px]">
        <div className="bg-[#f8f6f1] border border-[#e8e5de] rounded-lg p-2.5">
          <p className="text-[10px] uppercase tracking-wider text-[#8a8579]">Última captura</p>
          <p className="mt-0.5">{fmtDate(row.captured_at)}{row.captured_by ? ` · ${row.captured_by}` : ''}</p>
        </div>
        <div className="bg-[#f8f6f1] border border-[#e8e5de] rounded-lg p-2.5">
          <p className="text-[10px] uppercase tracking-wider text-[#8a8579]">{row.is_own ? 'Markers (base del sugerido)' : 'Ubicación'}</p>
          <p className="mt-0.5 truncate">{row.is_own ? (row.marker_names.join(', ') || 'Sin markers') : row.region || '—'}</p>
        </div>
      </div>
      {row.is_own && (
        <p className="text-[10.5px] text-[#8a8579] mt-2">Sugerido: Súper/Diesel = mínimo de markers; V-Power, Regular y ULSD por diferenciales de AD-P12.</p>
      )}
      {row.is_own && onChangePrices && (
        <button
          onClick={() => onChangePrices(row)}
          className="press mt-3 w-full h-10 rounded-xl bg-[#1c1c1c] hover:bg-black text-[#f8f6f1] text-[12.5px] font-medium inline-flex items-center justify-center gap-2"
          data-testid="monitor-change-prices-btn"
        >
          <PencilLine size={14}/> Cambiar precios (sin tracking)
        </button>
      )}
    </div>
  );
};

export default function AdminPriceMonitor() {
  const fuels = useFuels();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [dept, setDept] = useState(ALLV);
  const [zone, setZone] = useState(ALLV);
  const [tm, setTm] = useState(ALLV);
  const [kind, setKind] = useState(ALLV);
  const [flag, setFlag] = useState(null);
  const [selected, setSelected] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [changeRow, setChangeRow] = useState(null);
  const [changeOpen, setChangeOpen] = useState(false);

  const openChange = (r) => { setChangeRow(r); setChangeOpen(true); };

  const load = () => {
    setLoading(true);
    api.get('/prices/monitor').then(r => setData(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const rows = data?.rows || [];
  const opts = useMemo(() => ({
    depts: [...new Set(rows.map(r => r.department).filter(Boolean))].sort(),
    zones: [...new Set(rows.map(r => r.zone).filter(Boolean))].sort(),
    tms: [...new Set(rows.map(r => r.tm).filter(Boolean))].sort(),
  }), [rows]);

  const filtered = useMemo(() => rows.filter(r => {
    if (dept !== ALLV && r.department !== dept) return false;
    if (zone !== ALLV && r.zone !== zone) return false;
    if (tm !== ALLV && r.tm !== tm) return false;
    if (kind === 'own' && !r.is_own) return false;
    if (kind === 'comp' && r.is_own) return false;
    if (flag && !r.flags.includes(flag)) return false;
    if (q) {
      const t = q.toLowerCase();
      if (![r.name, r.brand, r.bu, r.region, r.department, r.zone, r.tm].some(x => (x || '').toLowerCase().includes(t))) return false;
    }
    return true;
  }), [rows, dept, zone, tm, kind, flag, q]);

  const counts = useMemo(() => {
    const c = { below: 0, mismatch: 0, stale: 0, gps: 0, no_data: 0 };
    rows.forEach(r => r.flags.forEach(f => { if (c[f] != null) c[f] += 1; }));
    return c;
  }, [rows]);

  const pick = (r) => { setSelected(r); if (window.innerWidth < 1024) setMobileOpen(true); };

  return (
    <div className="space-y-5" data-testid="admin-price-monitor-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-serif text-[36px] leading-tight tracking-tight">Monitor de precios</h2>
          <p className="text-[13px] text-[#5a5a5a] mt-1">Precios vigentes de todas las estaciones vs. sugeridos · {rows.length} estaciones</p>
        </div>
        <button onClick={load} className="press h-9 px-4 rounded-full bg-white border border-[#e8e5de] text-[12px] inline-flex items-center gap-1.5 hover:bg-[#f3f0e9]" data-testid="monitor-refresh">
          <RefreshCw size={13}/> Actualizar
        </button>
      </div>

      {/* Flag stat chips (click = filter) */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
        {Object.entries(FLAG_META).map(([k, m]) => {
          const I = m.icon; const active = flag === k;
          return (
            <button key={k} onClick={() => setFlag(active ? null : k)}
              className={`press text-left bg-white border rounded-xl p-3 transition ${active ? 'border-[#1c1c1c] ring-1 ring-[#1c1c1c]' : 'border-[#e8e5de] hover:border-[#cfcabe]'}`}
              data-testid={`flag-chip-${k}`}>
              <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full ${m.cls}`}><I size={10}/>{m.label}</span>
              <p className="font-serif text-[26px] leading-tight mt-1.5">{counts[k]}</p>
            </button>
          );
        })}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[180px]">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8a8579]"/>
          <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar estación, BU, marca…" className="h-9 pl-8 bg-white text-[12.5px]" data-testid="monitor-search"/>
        </div>
        {[['Depto.', dept, setDept, opts.depts, 'dept'], ['Zona', zone, setZone, opts.zones, 'zone'], ['TM', tm, setTm, opts.tms, 'tm']].map(([lbl, val, set, list, key]) => (
          <Select key={key} value={val} onValueChange={set}>
            <SelectTrigger className="h-9 w-[130px] bg-white text-[12px]" data-testid={`monitor-filter-${key}`}><SelectValue placeholder={lbl}/></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALLV}>{lbl}: Todos</SelectItem>
              {list.map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}
            </SelectContent>
          </Select>
        ))}
        <Select value={kind} onValueChange={setKind}>
          <SelectTrigger className="h-9 w-[140px] bg-white text-[12px]" data-testid="monitor-filter-kind"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALLV}>Todas</SelectItem>
            <SelectItem value="own">Propias</SelectItem>
            <SelectItem value="comp">Competencia</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="py-14 flex justify-center"><Loader2 className="animate-spin text-[#8a8579]"/></div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_420px] gap-4 items-start">
          {/* List */}
          <div className="bg-white border border-[#e8e5de] rounded-xl overflow-hidden">
            <div className="px-4 py-2.5 border-b border-[#e8e5de] text-[11px] text-[#8a8579]" data-testid="monitor-count">{filtered.length} estación(es)</div>
            <div className="max-h-[62vh] overflow-y-auto divide-y divide-[#f3f0e9]">
              {filtered.length === 0 && (
                <div className="py-10 text-center"><Inbox size={24} className="mx-auto text-[#a8a397] mb-1.5"/><p className="text-[12.5px] text-[#8a8579]">Sin estaciones con estos filtros.</p></div>
              )}
              {filtered.map(r => (
                <button key={r.id} onClick={() => pick(r)}
                  className={`w-full text-left px-4 py-3 hover:bg-[#f8f6f1] transition flex items-center gap-3 ${selected?.id === r.id ? 'bg-[#e7eeff]' : ''}`}
                  data-testid={`monitor-row-${r.id}`}>
                  <span className={`w-1.5 h-9 rounded-full shrink-0 ${r.is_own ? 'bg-[#0f1d6f]' : 'bg-[#b91c1c]'}`}/>
                  <span className="flex-1 min-w-0">
                    <span className="block text-[13px] font-medium truncate">{r.name}</span>
                    <span className="block text-[11px] text-[#8a8579] truncate">{r.brand}{r.bu ? ` · ${r.bu}` : ''}{r.zone ? ` · ${r.zone}` : ''}{r.department ? ` · ${r.department}` : ''}</span>
                  </span>
                  <span className="hidden sm:flex flex-wrap gap-1 justify-end max-w-[170px]">
                    {r.flags.slice(0, 2).map(f => {
                      const m = FLAG_META[f]; if (!m) return null;
                      return <span key={f} className={`text-[9.5px] px-1.5 py-0.5 rounded-full ${m.cls}`}>{m.label}</span>;
                    })}
                  </span>
                  <span className="text-right shrink-0">
                    <span className="block font-mono tabular-nums text-[13px]">{fmtQ(r.prices.super?.self ?? r.prices.super?.full ?? r.prices.diesel?.self)}</span>
                    <span className="block text-[10px] text-[#8a8579]">{r.captured_at ? new Date(r.captured_at).toLocaleDateString('es-GT', { day: '2-digit', month: '2-digit' }) : 'sin datos'}</span>
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Detail (desktop) */}
          <div className="hidden lg:block bg-[#f8f6f1] border border-[#e8e5de] rounded-xl p-4 sticky top-24">
            {selected ? <StationDetail row={selected} fuels={fuels} onChangePrices={openChange}/> : (
              <p className="text-[12.5px] text-[#8a8579] py-10 text-center">Selecciona una estación para ver sus precios vigentes.</p>
            )}
          </div>
        </div>
      )}

      {/* Detail (mobile) */}
      <Dialog open={mobileOpen} onOpenChange={setMobileOpen}>
        <DialogContent className="max-w-[95vw] sm:max-w-lg bg-[#f8f6f1] max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="sr-only">Detalle de estación</DialogTitle></DialogHeader>
          <StationDetail row={selected} fuels={fuels} onChangePrices={(r) => { setMobileOpen(false); openChange(r); }}/>
        </DialogContent>
      </Dialog>

      <ManualPriceChangeModal open={changeOpen} onOpenChange={setChangeOpen} row={changeRow} fuels={fuels} onDone={load}/>
    </div>
  );
}
